import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCounties } from "@/hooks/use-counties";
import { useHomeCareServiceOptions, type HomeCareFilters } from "@/hooks/use-home-care-providers";

interface Props {
  filters: HomeCareFilters;
  onChange: (filters: HomeCareFilters) => void;
}

export function HomeCareFiltersBar({ filters, onChange }: Props) {
  const { data: counties } = useCounties();
  const { data: serviceOptions } = useHomeCareServiceOptions();

  const activeCount = [
    filters.county,
    filters.service,
    filters.hseApprovedOnly,
    filters.minRating,
  ].filter(Boolean).length;

  const clearAll = () =>
    onChange({
      search: "",
      county: null,
      service: null,
      hseApprovedOnly: false,
      minRating: null,
    });

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search providers by name, county..."
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          className="pl-10"
        />
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* County */}
        <Select
          value={filters.county || "all"}
          onValueChange={(v) =>
            onChange({ ...filters, county: v === "all" ? null : v })
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Counties" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Counties</SelectItem>
            {counties?.map((c) => (
              <SelectItem key={c.slug} value={c.slug}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Service */}
        <Select
          value={filters.service || "all"}
          onValueChange={(v) =>
            onChange({ ...filters, service: v === "all" ? null : v })
          }
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Services" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Services</SelectItem>
            {serviceOptions?.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Rating */}
        <Select
          value={filters.minRating?.toString() || "any"}
          onValueChange={(v) =>
            onChange({ ...filters, minRating: v === "any" ? null : Number(v) })
          }
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Any Rating" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any Rating</SelectItem>
            <SelectItem value="3">3+ Stars</SelectItem>
            <SelectItem value="4">4+ Stars</SelectItem>
            <SelectItem value="4.5">4.5+ Stars</SelectItem>
          </SelectContent>
        </Select>

        {/* HSE toggle */}
        <div className="flex items-center gap-2">
          <Switch
            id="hse-filter"
            checked={filters.hseApprovedOnly}
            onCheckedChange={(checked) =>
              onChange({ ...filters, hseApprovedOnly: checked })
            }
          />
          <Label htmlFor="hse-filter" className="text-sm cursor-pointer whitespace-nowrap">
            HSE Approved
          </Label>
        </div>

        {/* Clear */}
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="gap-1">
            <X className="h-3.5 w-3.5" />
            Clear ({activeCount})
          </Button>
        )}
      </div>
    </div>
  );
}
